/**
 * Søknader as an action queue (owner, 2026-09-21): what needs an answer, what is coming, and
 * what is done. Pure, so the three lists and what a request collides with are tested without
 * a screen.
 */
import type { ApiAbsence, ApiShift, ApiWorkTimeChange } from '@/api/data';
import { oslo } from '@/rules';
import { inRange } from '@timefly/core';
import type { Kind } from './AbsenceRange';
import { spanOf } from './dates';

export type Bucket = 'handling' | 'kommende' | 'historikk';
export const BUCKETS: [Bucket, string][] = [
  ['handling', 'Krever handling'],
  ['kommende', 'Kommende'],
  ['historikk', 'Historikk'],
];

/**
 * Which list a request belongs in. Pending needs an answer. An approved absence the employee has
 * asked to cancel needs one too, until it is over. Approved and not started is coming; the rest
 * (declined, started, over) is history.
 */
export function bucketOf(
  a: Pick<ApiAbsence, 'status' | 'start_date' | 'end_date'>,
  today: string,
  cancelAsked = false,
): Bucket {
  const { from, to } = spanOf(a);
  if (a.status === 'pending') return 'handling';
  if (a.status === 'accepted' && cancelAsked && to >= today) return 'handling';
  if (a.status === 'accepted' && from > today) return 'kommende';
  return 'historikk';
}

/** One list, in the order it is worked: the queue and what is coming soonest first, history newest first. */
export function queueOf<A extends Pick<ApiAbsence, 'id' | 'status' | 'start_date' | 'end_date'>>(
  all: A[],
  bucket: Bucket,
  today: string,
  cancelIds: ReadonlySet<number> = new Set(),
): A[] {
  const list = all.filter((a) => bucketOf(a, today, cancelIds.has(a.id)) === bucket);
  return list.sort((a, b) =>
    bucket === 'historikk'
      ? b.start_date.localeCompare(a.start_date)
      : a.start_date.localeCompare(b.start_date),
  );
}

/**
 * The person's published shifts inside a request. A full day is matched by date; a clock range by
 * instant, so a 10:00 to 12:00 request names the shift it cuts into and not every shift that day.
 * The drawer reads a draft the same way.
 */
export function conflictsOf(
  a: Pick<ApiAbsence, 'user_id' | 'start_date' | 'end_date'>,
  kind: Kind,
  shifts: Pick<ApiShift, 'user_id' | 'published' | 'start_time' | 'end_time'>[],
) {
  const own = shifts.filter((s) => s.user_id === a.user_id && s.published);
  if (kind === 'date') {
    const { from, to } = spanOf(a);
    return own.filter((s) => inRange(oslo(s.start_time)?.date ?? '', from, to));
  }
  const s0 = Date.parse(a.start_date);
  const e0 = Date.parse(a.end_date);
  return own.filter((s) => Date.parse(s.start_time) < e0 && Date.parse(s.end_time) > s0);
}

/**
 * The approved absences the employee has asked to cancel and nobody has answered: an
 * 'absence_cancel_requested' history row newer than the absence's last write. A decision or an
 * edit writes the row (updated_at); the employee's request does not, so a request older than the
 * last write has been dealt with, and one on an absence no longer approved is moot.
 */
export function cancelIdsOf(
  rows: Pick<ApiWorkTimeChange, 'absence_id' | 'action' | 'created_at'>[],
  absences: Pick<ApiAbsence, 'id' | 'status' | 'updated_at'>[],
): Set<number> {
  const out = new Set<number>();
  for (const r of rows) {
    if (r.action !== 'absence_cancel_requested' || r.absence_id == null) continue;
    const a = absences.find((x) => x.id === r.absence_id);
    if (a?.status === 'accepted' && (!a.updated_at || r.created_at >= a.updated_at)) out.add(a.id);
  }
  return out;
}
